<script setup lang="ts">
import type { SpecialCharacters } from "@/lib/api-client";

const modelValue = defineModel<string | undefined>({
	default: "",
});

const props = withDefaults(
	defineProps<{
		selectValue?: string;
		specialCharacters: SpecialCharacters;
		selectOptions?: Map<string, string>;
		placeholder?: string;
		submitButtonLabel?: string;
	}>(),
	{
		selectValue: "",
		selectOptions: () => new Map<string, string>([]),
		placeholder: "",
		submitButtonLabel: "",
	},
);
const emit = defineEmits(["update:modelValue", "update:selectValue", "submit"]);

const inputElement = ref();

const submit = () => {
	emit("submit", modelValue.value);
};

const InsertSnippet = async (snippet: string): Promise<void> => {
	if (typeof inputElement.value.selectionStart !== "undefined") {
		modelValue.value ??= "";
		const selectionStart = Number(inputElement.value.selectionStart),
			selectionEnd = Number(inputElement.value.selectionEnd);
		modelValue.value =
			modelValue.value.substring(0, selectionStart) +
			snippet +
			modelValue.value.substring(selectionEnd, modelValue.value.length);
		await nextTick();
		restoreCursorPosition(selectionStart + snippet.length);
	}
};

const restoreCursorPosition = (pos: number) => {
	if (typeof inputElement.value.createTextRange !== "undefined") {
		const range = inputElement.value.createTextRange();
		range.move("character", pos);
		range.select();
	} else if (typeof inputElement.value.setSelectionRange !== "undefined") {
		inputElement.value.focus();
		inputElement.value.setSelectionRange(pos, pos);
	} else if (typeof inputElement.value.selectionStart !== "undefined") {
		inputElement.value.focus();
		inputElement.value.selectionStart = pos;
		inputElement.value.selectionEnd = pos;
	}
};

const mySelectOption = ref(
	props.selectOptions.has(String(props.selectValue))
		? String(props.selectValue)
		: props.selectOptions.size > 0
			? props.selectOptions.keys().next().value
			: undefined,
);
watch(
	mySelectOption,
	(val) => {
		emit("update:selectValue", val);
	},
	{ immediate: true },
);
watch(
	() => props.selectValue,
	(val) => {
		if (props.selectOptions.has(String(val))) {
			mySelectOption.value = val;
		}
	},
);
</script>

<template>
	<!-- eslint-disable tailwindcss/no-custom-classname -->
	<div class="ib">
		<div class="ib-buttons">
			<!-- eslint-disable vue/no-v-html -->
			<button
				v-for="(c, i) in specialCharacters"
				:key="i"
				@click.prevent="InsertSnippet(c.value)"
				v-html="c.text ? c.text : c.value"
			></button>
			<!-- eslint-enable vue/no-v-html -->
		</div>
		<div class="ib-input-row">
			<div class="ib-textinput">
				<!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
				<input
					ref="inputElement"
					v-model="modelValue"
					:placeholder="placeholder"
					type="text"
					@keydown.enter.prevent="submit"
				/>
			</div>
			<div v-if="props.selectOptions.size > 0" class="ib-select">
				<!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
				<select v-model="mySelectOption">
					<option v-for="([key, value], i) in props.selectOptions" :key="i" :value="key">
						{{ value }}
					</option>
				</select>
			</div>
			<div v-if="!!props.submitButtonLabel" class="ib-submit">
				<button type="button" @click.prevent="submit">{{ props.submitButtonLabel }}</button>
			</div>
		</div>
	</div>
</template>
